import { Button, message, Modal } from "antd";
import React, { useEffect, useState } from "react";
import ComplianceService from "Services/EmployeeServices/ComplianceService";
import dayjs from "dayjs";
import { Tag, Descriptions, Timeline } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CalendarOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { DownloadOutlined } from "@mui/icons-material";

function useCompliance() {
  const [allCompliances, setAllCompliances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acknowledgeLoading, setAcknowledgeLoading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedCompliance, setSelectedCompliance] = useState(null);

  const [policyDetails, setPolicyDetails] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);


  // Fetch pending compliances
  const fetchAllCompliances = async () => {
    setLoading(true);
    try {
      
      const response = await ComplianceService.getAllCompliances();
      setAllCompliances(response);
    } catch (error) {
      console.error("Error fetching pending compliances:", error);
      message.error("Failed to load pending policies");
    } finally {
      setLoading(false);
    }
  };
  // Handle acknowledge compliance
  const handleAcknowledge = (compliance) => {
    setSelectedCompliance(compliance);
    setConfirmModalVisible(true);
  };
  // Handle view details
  const handleViewDetails = async (compliance) => {
    try {
      // Check if we have valid data
      if (!compliance || (!compliance.policyId && !compliance.id)) {
        message.error("Invalid policy data");
        return;
      }

      const complianceId = compliance.complianceId;
      const details = await ComplianceService.getPolicyWithVersions(complianceId);

      // Check if we got valid response
      if (!details) {
        message.error("No policy details found");
        return;
      }

      setPolicyDetails(details);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error("Error fetching policy details:", error);
      message.error("Failed to load policy details");
    }
  };
  // (complete download function):
  const handleDownload = async (versionId) => {
    try {
      const response = await ComplianceService.downloadPolicy(versionId);

      // Check if we have valid response data
      if (!response.data) {
        message.error("No file data received");
        return;
      }

      // Create blob from response data
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });

      // Check blob size
      if (blob.size === 0) {
        message.error("Received empty file");
        return;
      }

      // Create download URL
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement("a");
      link.href = url;

      // Get filename from response headers or use default
      let filename = `policy-version-${versionId}.pdf`;
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      message.success(`Downloaded: ${filename}`);
    } catch (error) {
      console.error("Error downloading file:", error);

      // More specific error messages
      if (error.response?.status === 404) {
        message.error("File not found on server");
      } else if (error.response?.status === 500) {
        message.error("Server error while downloading file");
      } else {
        message.error("Failed to download file");
      }
    }
  };
  // Confirm acknowledgement
  const handleConfirmAcknowledge = async () => {
    if (!selectedCompliance) return;

    try {
      setAcknowledgeLoading(true);
      
      await ComplianceService.acknowledgeCompliance( selectedCompliance.id);

      message.success("Policy acknowledged successfully");
      setConfirmModalVisible(false);
      setSelectedCompliance(null);

      await fetchAllCompliances();
    } catch (error) {
      console.error("Error acknowledging compliance:", error);
      message.error("Failed to acknowledge policy");
    } finally {
      setAcknowledgeLoading(false);
    }
  };
  // Table columns for pending compliances
  const columns = [
    {
      title: "Policy Name",
      dataIndex: "policyName",
      key: "policyName",
      render: (text, record) => (
        <div style={{ fontWeight: 500 }}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          {text}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button className="btn-back" onClick={() => handleViewDetails(record)}>
            View Detail
          </Button>

          <Button
            className="btn-primary"
            icon={record.isAcknowledged ? <CheckCircleOutlined /> : ""}
            onClick={() => handleAcknowledge(record)}
            //   style={{ backgroundColor: '#3d556cff', color:'white', borderColor: '#edf2f6ff' }}
            disabled={record.isAcknowledged}
          >
            {record.isAcknowledged ? "Agreed" : "I Agree"}
          </Button>
        </div>
      ),
    },
  ];

  const DetailsModal = () => (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", fontSize: 18, fontWeight: 600 }}>
          <InfoCircleOutlined style={{ marginRight: 12, fontSize: 20, color: "#599c16" }} />
          Policy Details:
          <span style={{ marginLeft: 12, color: "#333" }}>{policyDetails?.policyName}</span>
        </div>
      }
      open={detailsModalVisible}
      onCancel={() => {
        setDetailsModalVisible(false);
        setPolicyDetails(null);
      }}
      footer={
        <div style={{ textAlign: "right", paddingTop: 12 }}>
          <Button
            type="default"
            onClick={() => {
              setDetailsModalVisible(false);
              setPolicyDetails(null);
            }}
            style={{
              minWidth: 110,
              background: "#599c16",
              color: "white",
              borderRadius: 6,
              fontWeight: 500,
            }}
          >
            Close
          </Button>
        </div>
      }
      width={1100}
      style={{ left: "80px" }}
      styles={{
        body: {
          maxHeight: "calc(90vh - 108px)",
          overflowY: "auto",
          padding: "24px 28px",
          background: "#fafafa",
        },
      }}
      destroyOnClose
    >
      {policyDetails && (
        <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
          {/* --- Policy Info Section --- */}
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 20,
                paddingBottom: 12,
                borderBottom: "2px solid #e8e8e8",
              }}
            >
              <FileTextOutlined style={{ marginRight: 10, fontSize: 18, color: "#599c16" }} />
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1b1b1b" }}>
                Policy Information
              </h3>
            </div>

            <Descriptions
              bordered
              column={2}
              size="middle"
              labelStyle={{
                fontWeight: 600,
                backgroundColor: "#f4f6f8",
                width: 160,
                fontSize: 14,
              }}
            className="" 
            
            >
              <Descriptions.Item label="Policy Name" span={2}>
                <strong>{policyDetails.policyName}</strong>
              </Descriptions.Item>

              <Descriptions.Item label="Description" span={2}>
                <div
                  style={{
                    maxHeight: 120,
                    overflowY: "auto",
                    padding: "4px 0",
                  }}
                >
                  {policyDetails.description || (
                    <span style={{ color: "#888", fontStyle: "italic" }}>
                      No description provided
                    </span>
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                <Tag color="#599c16" style={{ fontWeight: 500 }}>
                  Active
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Created On">
                <CalendarOutlined style={{ marginRight: 8, color: "#599c16" }} />
                {policyDetails.createdOn
                  ? dayjs(policyDetails.createdOn).format("MMMM DD, YYYY HH:mm")
                  : "Unknown"}
              </Descriptions.Item>

              <Descriptions.Item label="Last Modified">
                <CalendarOutlined style={{ marginRight: 8, color: "#599c16" }} />
                {policyDetails.lastModifiedOn
                  ? dayjs(policyDetails.lastModifiedOn).format("MMMM DD, YYYY HH:mm")
                  : "Unknown"}
              </Descriptions.Item>
            </Descriptions>
          </section>

          {/* --- Version History Section --- */}
          <section>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
                paddingBottom: 12,
                borderBottom: "2px solid #e8e8e8",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <HistoryOutlined style={{ marginRight: 10, fontSize: 18, color: "#599c16" }} />
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1b1b1b" }}>
                  Version History
                </h3>
              </div>
              <Tag style={{ fontWeight: 500, padding: "4px 10px" }}>
                {policyDetails.versions?.length || 0} versions
              </Tag>
            </div>

            {policyDetails.versions?.length > 0 ? (
              <div style={{ maxHeight: 320, overflowY: "auto", paddingRight: 10 }}>
                <Timeline
                  style={{ marginLeft: 16 }}
                  items={policyDetails.versions.map((version, index) => ({
                    key: version.id ?? index,
                    dot: version.isActive ? (
                      <CheckCircleOutlined style={{ fontSize: 16, color: "#599c16" }} />
                    ) : (
                      <FileTextOutlined style={{ fontSize: 14 }} />
                    ),
                    children: (
                      <div
                        style={{
                          background: "#fff",
                          padding: "16px 20px",
                          borderRadius: 8,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        {/* Version Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 12,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <strong>Version {policyDetails.versions.length - index}</strong>
                            {version.isActive && (
                              <Tag color="#599c16" style={{ fontWeight: 500 }}>
                                Active
                              </Tag>
                            )}
                          </div>
                        </div>

                        {/* Version Details */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: 12,
                            marginBottom: 16,
                            fontSize: 13,
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: 600, color: "#595959" }}>
                              <CalendarOutlined style={{ marginRight: 6 }} />
                              Effective Date:
                            </span>{" "}
                            {version.effectiveDate
                              ? dayjs(version.effectiveDate).format("MMM DD, YYYY")
                              : "N/A"}
                          </div>
                        </div>

                        {/* Download Button */}
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload(version.id)}
                          style={{
                            borderRadius: 6,
                            background: "#599c16",
                            color: "white",
                            fontWeight: 500,
                          }}
                        >
                          Download
                        </Button>
                      </div>
                    ),
                  }))}
                />
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#6c757d",
                  background: "#f8f9fa",
                  borderRadius: 8,
                  border: "1px dashed #dee2e6",
                }}
              >
                <FileTextOutlined style={{ fontSize: 32, marginBottom: 12, color: "#adb5bd" }} />
                <div style={{ fontSize: 14, fontWeight: 500 }}>No versions available</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>This policy has no version history</div>
              </div>
            )}
          </section>
        </div>
      )}
    </Modal>
  );

  // Acknowledgement Confirmation Modal
  const ConfirmModal = () => (
    <Modal
      title={
        <div>
          <ExclamationCircleOutlined style={{ marginRight: 8, color: "#faad14" }} />
          Confirm Acknowledgement
        </div>
      }
      open={confirmModalVisible}
      onCancel={() => {
        setConfirmModalVisible(false);
        setSelectedCompliance(null);
      }}
      footer={
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              setConfirmModalVisible(false);
              setSelectedCompliance(null);
            }}
          >
            Cancel
          </Button>
          <Button
            className="btn-primary"
            loading={acknowledgeLoading}
            onClick={handleConfirmAcknowledge}
            style={{ backgroundColor: "#3d556cff", borderColor: "#f2f6f0ff" }}
          >
            Yes, I Agree
          </Button>
        </div>
      }
      width={500}
    >
      <div style={{ padding: "16px 0" }}>
        <p>
          <strong>Are you sure you want to acknowledge this policy?</strong>
        </p>
        <p>By acknowledging, you confirm that you have read and understood the policy.</p>
        {selectedCompliance && (
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px",
              borderRadius: "6px",
              marginTop: "12px",
            }}
          >
            <p>
              <strong>Policy:</strong> {selectedCompliance.policyName}
            </p>
            <p>
              <strong>Effective:</strong>{" "}
              {selectedCompliance.effectiveDate
                ? dayjs(selectedCompliance.effectiveDate).format("MMMM DD, YYYY")
                : "N/A"}
            </p>
            <p>
              <strong>Expiry:</strong>{" "}
              {selectedCompliance.expiryDate
                ? dayjs(selectedCompliance.expiryDate).format("MMMM DD, YYYY")
                : "N/A"}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );

  // Fetch data on component mount
  useEffect(() => {
    fetchAllCompliances();
  }, []);

  return {
    allCompliances,
    loading,
    columns,
    DetailsModal,
    ConfirmModal,
  };
}

export default useCompliance;
